import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';
import AntCondition from '../utils/AntCondition';
import AntBaseForm from './AntBaseForm';
import AntGrid from './AntGrid';
import AntTabs from './AntTabs';

const DEFAULT_VOUCHER_GRID_PAGE_SIZE = 30;
const DEFAULT_VOUCHER_GRID_PAGE_SIZES = [15, 30, 50];

export default forwardRef(function (props, ref) {
    const [shouldRender, setShouldRender] = useState(1);
    const isRender = shouldRender + (props.shouldRender ? props.shouldRender : 1);
    const forceRender = useCallback(() => {
        setShouldRender((v) => v + 1);
    }, []);

    const initMainTabs = useCallback(() => {
        const hasRecord = props.formData && (props.formData.id || props.formData.code);

        const hasVisibleItems = (items) => {
            if (!(items instanceof Array) || items.length === 0) return false;
            return items.some((item) => {
                if (item.itemType === 'group' && item.items instanceof Array) return hasVisibleItems(item.items);
                if (item.itemType === 'empty' || !item.dataField) return false;
                // Nếu đang xem record cụ thể, kiểm tra field có trong formData không
                if (hasRecord && !(item.dataField in props.formData)) return false;
                return AntCondition.isVisible(props.formData, item) !== false;
            });
        };

        var tabs = [];

        if (props.tabs instanceof Array) {
            tabs = props.tabs.filter((tab) => {
                if (!AntCondition.isVisible(props.formData, tab)) return false;

                // Tabs có grid luôn hiển thị (data phụ thuộc vào runtime)
                if (tab.grid) return true;

                // Tabs render component tuỳ biến (tab.render) luôn hiển thị
                if (typeof tab.render === 'function') return true;

                // Tabs chỉ có header/footer: ẩn nếu tất cả items đều bị ẩn
                var hasHeader = tab.header && tab.header.items instanceof Array;
                var hasFooter = tab.footer && tab.footer.items instanceof Array;
                if (!hasHeader && !hasFooter) return true;

                return hasVisibleItems(tab.header?.items) || hasVisibleItems(tab.footer?.items);
            });
        }
        return tabs;
    }, [props.formData]);

    const mainTabs = useMemo(() => initMainTabs(), [props.formData]);

    // Tab đang chọn — chỉ dùng để hoãn lưới khác khi accordion mở một mục mỗi lần.
    // Giữ trong ref nên việc đổi tab không kéo theo lượt render nào.
    const activeTabName = useRef(null);
    // Khi accordion cho mở nhiều mục, tab không được chọn vẫn có thể đang hiển thị.
    // Chỉ hoãn reload nếu chắc chắn tại một thời điểm chỉ có một mục mở.
    const isTabActive = useCallback(
        (tabName) =>
            props.autoExpandAll !== false ||
            props.groupExpandMultiple === true ||
            !activeTabName.current ||
            !tabName ||
            activeTabName.current === tabName,
        [props.autoExpandAll, props.groupExpandMultiple]
    );

    const tabHeaders = useRef([]);
    const tabFooters = useRef([]);
    const tabGrids = useRef([]);

    // Mở tab nào thì lưới của tab đó nạp bù đúng một lượt, nếu nó còn nợ.
    const onTabChanged = useCallback((tabName) => {
        activeTabName.current = tabName;
        const grids = tabGrids.current instanceof Array ? tabGrids.current : [];
        grids.filter((grid) => grid && grid.tabName === tabName).forEach((grid) => grid?.ref?.current?.reloadIfPending?.());
    }, []);

    const isGridAllowQuery = useCallback(
        (tabGrid) => {
            if (
                !tabGrid ||
                !tabGrid.FKField ||
                (!tabGrid.parentIDField && !props.formData.code) ||
                (tabGrid.parentIDField && (!props.formData || !props.formData[tabGrid.parentIDField]))
            ) {
                return false;
            }

            return true;
        },
        [props.formData]
    );

    const getFKData = useCallback(
        (tabGrid) => {
            // if (!tabGrid || !tabGrid.FKField || !props.formData.code) {
            if (
                !tabGrid ||
                !tabGrid.FKField ||
                (!tabGrid.parentIDField && !props.formData.code) ||
                (tabGrid.parentIDField && (!props.formData || !props.formData[tabGrid.parentIDField]))
            ) {
                return {
                    // notAllowQuery:true
                };
            }

            var childValue = tabGrid.parentIDField ? (props.formData ? props.formData[tabGrid.parentIDField] : undefined) : props.formData.code;

            return {
                [tabGrid.FKField]: childValue
            };
        },
        [props.formData]
    );

    const onRenderTab = useCallback(
        (tabArg) => {
            if (mainTabs.length <= 0 || props.tabs instanceof Array === false || props.tabs.length <= 0) return <div></div>;

            // AntAccordion truyền tên tab, hoặc chính object tab khi tab không đặt name → quy về tab + khoá theo chỉ số
            let tab;
            let tabName = tabArg;
            if (tabArg && typeof tabArg === 'object') {
                tab = props.tabs.find((item) => item === tabArg) || tabArg;
                const tabIndex = props.tabs.indexOf(tab);
                tabName = tab.name ? tab.name : String(tabIndex >= 0 ? tabIndex : 0);
            } else {
                tab = props.tabs.find((item, index) => item.name === tabName || (!item.name && String(index) === tabName));
            }
            if (!tab) return <div></div>;

            let isVisible = AntCondition.isVisible(props.formData, tab);
            if (!isVisible) return <div></div>;

            // Tab render component tuỳ biến (nhận formData) — bỏ qua form/grid mặc định.
            if (typeof tab.render === 'function') return <React.Fragment>{tab.render(props.formData)}</React.Fragment>;

            let tabHeader = tab.header;
            let tabFooter = tab.footer;
            if (tabHeader) tabHeader = JSON.parse(stringify(tabHeader));
            if (tabFooter) tabFooter = JSON.parse(stringify(tabFooter));

            let tabGrid = tab.grid;
            let tabGridRawCols = tabGrid && tabGrid.columns instanceof Array ? tabGrid.columns : [];
            let tabGridCols = tabGrid && tabGrid.columns instanceof Array ? tabGrid.columns : [];
            let tabGridActions = tabGrid && tabGrid.actionItems instanceof Array ? tabGrid.actionItems : [];
            tabGridRawCols = JSON.parse(stringify(tabGridRawCols));
            tabGridCols = JSON.parse(stringify(tabGridCols));
            tabGridActions = JSON.parse(stringify(tabGridActions));

            let isVisibleTabHeader = false;
            let isVisibleTabGrid = false;
            let isVisibleTabFooter = false;

            let header = null;
            let footer = null;
            let grid = null;

            let isCanAdd = props.formData && props.formData.edit && props.formData.edit.isEditable && props.formData.edit.withChildren;

            if (isCanAdd && typeof props.formData.edit.withChildren === 'object' && props.formData.edit.withChildren.canAdd !== undefined)
                isCanAdd = Boolean(props.formData.edit.withChildren.canAdd);
            else if (isCanAdd && props.formData.edit.withChildren instanceof Array && tabGrid) {
                let gridTableName = tabGrid && tabGrid.table ? tabGrid.table : tabGrid.API ? tabGrid.API.entityName : props.table;
                let found = gridTableName ? props.formData.edit.withChildren.find((f) => f.table === gridTableName) : false;
                isCanAdd = found ? Boolean(found.canAdd) : false;
            }

            let isCanEdit = props.formData && props.formData.edit && props.formData.edit.isEditable && props.formData.edit.withChildren;
            if (isCanEdit && typeof props.formData.edit.withChildren === 'object' && props.formData.edit.withChildren.canEdit !== undefined)
                isCanEdit = Boolean(props.formData.edit.withChildren.canEdit);
            else if (isCanEdit && props.formData.edit.withChildren instanceof Array && tabGrid) {
                let gridTableName = tabGrid && tabGrid.table ? tabGrid.table : tabGrid.API ? tabGrid.API.entityName : props.table;
                let found = gridTableName ? props.formData.edit.withChildren.find((f) => f.table === gridTableName) : false;
                isCanEdit = found ? Boolean(found.canEdit) : false;
            }

            let isCanDelete = props.formData && props.formData.edit && props.formData.edit.isEditable && props.formData.edit.withChildren;
            if (isCanDelete && typeof props.formData.edit.withChildren === 'object' && props.formData.edit.withChildren.canDelete !== undefined)
                isCanDelete = Boolean(props.formData.edit.withChildren.canDelete);
            else if (isCanDelete && props.formData.edit.withChildren instanceof Array && tabGrid) {
                let gridTableName = tabGrid && tabGrid.table ? tabGrid.table : tabGrid.API ? tabGrid.API.entityName : props.table;
                let found = gridTableName ? props.formData.edit.withChildren.find((f) => f.table === gridTableName) : false;
                isCanDelete = found ? Boolean(found.canDelete) : false;
            }

            tabGridActions = tabGridActions.filter((a) => {
                let isVisible = AntCondition.isVisible(props.formData, a);

                if (
                    isVisible &&
                    a.visibleOnMain &&
                    props.formData &&
                    Object.keys(props.formData).length > 0 &&
                    (props.formData.id || props.formData.code)
                ) {
                    let vObj = a.visibleOnMain;
                    if (a.visibleOnMain instanceof Array) {
                        vObj = a.visibleOnMain.find(
                            (v) =>
                                v.table === props.formTable ||
                                (v.table instanceof Array && v.table.includes(props.formTable)) ||
                                (v.tables instanceof Array && v.tables.includes(props.formTable))
                        );

                        if (vObj) vObj = vObj.value;
                    }
                    if (vObj)
                        isVisible = AntCondition.isVisible(props.formData, {
                            visible: vObj
                        });
                }
                return isVisible;
            });

            tabGridActions = tabGridActions.map((gr) => {
                if (gr.items instanceof Array) {
                    gr.items = gr.items.filter((a) => {
                        let isVisible = AntCondition.isVisible(props.formData, a);

                        if (
                            isVisible &&
                            a.visibleOnMain &&
                            props.formData &&
                            Object.keys(props.formData).length > 0 &&
                            (props.formData.id || props.formData.code)
                        ) {
                            let vObj = a.visibleOnMain;
                            if (a.visibleOnMain instanceof Array) {
                                vObj = a.visibleOnMain.find(
                                    (v) =>
                                        v.table === props.formTable ||
                                        (v.table instanceof Array && v.table.includes(props.formTable)) ||
                                        (v.tables instanceof Array && v.tables.includes(props.formTable))
                                );

                                if (vObj) vObj = vObj.value;
                            }
                            if (vObj)
                                isVisible = AntCondition.isVisible(props.formData, {
                                    visible: vObj
                                });
                        }
                        return isVisible;
                    });
                }
                return gr;
            });

            tabGridActions = tabGridActions.filter((a) => {
                let isActionVisible = true;
                if (a.equivalentParentActions instanceof Array) {
                    isActionVisible = false;
                    if (props.formData?.actions?.some((k) => a.equivalentParentActions.includes(k.name) && k.visible)) {
                        isActionVisible = true;
                    }
                }
                if (!isActionVisible) return false;

                return (
                    a.equivalentActions instanceof Array === false ||
                    (a.equivalentActions.includes('add') && isCanAdd) ||
                    (a.equivalentActions.includes('edit') && isCanEdit) ||
                    (a.equivalentActions.includes('delete') && isCanDelete)
                );
            });

            let isCanImportCreate = Boolean(
                tabGrid &&
                    (tabGrid.showImport || isCanAdd) &&
                    tabGrid.import !== undefined &&
                    (tabGrid.import.createable !== undefined ? tabGrid.import.createable : false)
            );
            let isCanImportUpdate = Boolean(
                tabGrid &&
                    (tabGrid.showImport || isCanEdit) &&
                    tabGrid.import !== undefined &&
                    (tabGrid.import.updateable !== undefined ? tabGrid.import.updateable : false)
            );

            let isCanImport = isCanImportCreate || isCanImportUpdate;

            let showAdd =
                props.disabled !== true && isCanAdd && tabGrid && tabGrid.add !== undefined && AntCondition.isVisible(props.formData, tabGrid.add);

            let hasEditAction =
                props.disabled !== true && tabGrid && tabGrid.edit !== undefined && AntCondition.isVisible(props.formData, tabGrid.edit);
            let hasDeleteAction =
                props.disabled !== true &&
                tabGrid &&
                tabGrid.delete !== undefined &&
                AntCondition.isVisible(props.formData, tabGrid.delete);
            let showEdit = Boolean(isCanEdit && hasEditAction);
            let showDelete = Boolean(isCanDelete && hasDeleteAction);
            let hasBatchSelectionIntent = Boolean(
                tabGrid &&
                    ((tabGrid.batchActions instanceof Array && tabGrid.batchActions.length > 0) || tabGrid.autoBatchActions === true)
            );
            let showEditOnActionBar = hasBatchSelectionIntent ? Boolean(hasEditAction) : showEdit;
            let showDeleteOnActionBar = hasBatchSelectionIntent ? Boolean(hasDeleteAction) : showDelete;
            let showImport =
                props.disabled !== true &&
                isCanImport &&
                tabGrid &&
                tabGrid.import !== undefined &&
                AntCondition.isVisible(props.formData, tabGrid.import);

            let isCanSelect = props.disabled !== true && (showEdit || showDelete);

            if (tabHeader) {
                let headerName = tabHeader.name ? tabHeader.name : tabName;
                header = tabHeaders.current.find((r) => r.name === headerName);
                if (!header) {
                    header = {
                        tabName: tabName,
                        name: headerName,
                        props: tabHeader,
                        tabProps: tab,
                        ref: React.createRef()
                    };
                    tabHeaders.current.push(header);
                }
            }

            if (tabFooter) {
                let footerName = tabFooter.name ? tabFooter.name : tabName;
                footer = tabFooters.current.find((r) => r.name === footerName);
                if (!footer) {
                    footer = {
                        tabName: tabName,
                        name: footerName,
                        props: tabFooter,
                        tabProps: tab,
                        ref: React.createRef()
                    };
                    tabFooters.current.push(footer);
                }
            }
            let gridName = null;
            if (tabGrid) {
                gridName = String(tabGrid.name ? tabGrid.name : tabName);
                grid = tabGrids.current.find((r) => r.name === gridName);
                if (!grid) {
                    grid = {
                        tabName: tabName,
                        name: gridName,
                        props: tabGrid,
                        tabProps: tab,
                        ref: React.createRef()
                    };
                    tabGrids.current.push(grid);
                }

                tabGrid = grid.props;

                tabGridCols = tabGridCols.slice().filter((col) => {
                    return AntCondition.isRender(props.formData, col);
                });

                tabGridCols.forEach((col) => {
                    // `editable` của CẤU HÌNH gốc, chụp một lần: dòng dưới ghi đè col.editable mỗi lần render nên
                    // rawConfig lần sau đã mang giá trị tính (false). Lưới nhập trực tiếp cần biết cột nào bị
                    // cấu hình khoá thật (editable: false) — null = không cấu hình.
                    if (!Object.prototype.hasOwnProperty.call(col, '_configEditable')) col._configEditable = col.editable === undefined ? null : col.editable;
                    // if (typeof col.visible !== 'object' || col.visible.type !== 'action' || !col.visible.field)
                    col.rawConfig = JSON.parse(stringify(col));
                    col.visible = AntCondition.isVisible(props.formData, col);
                    col.editable = isCanEdit && AntCondition.isEditable(props.formData, col);
                });
            }

            isVisibleTabHeader = tabHeader && AntCondition.isVisible(props.formData, tabHeader);
            isVisibleTabFooter = tabFooter && AntCondition.isVisible(props.formData, tabFooter);
            isVisibleTabGrid = tabGrid && AntCondition.isVisible(props.formData, tabGrid);

            let searchFields = [];

            if (tabGrid && tabGrid.FKField) searchFields.push(tabGrid.FKField);

            if (tabGrid && tabGrid.searchFields instanceof Array)
                tabGrid.searchFields.forEach((f) => {
                    searchFields.push(f);
                });
            if (tabGrid && typeof tabGrid.searchConditions === 'object' && tabGrid.searchConditions !== null) {
                Object.keys(tabGrid.searchConditions).map((f) => {
                    searchFields.push(f);
                });
            }
            let showScanBarcode = tabGrid && tabGrid.showScanBarcode !== undefined ? tabGrid.showScanBarcode : false;
            if (tabGrid && tabGrid.showScanBarcode && typeof tabGrid.showScanBarcode === 'object')
                showScanBarcode = AntCondition.isValid(props.formData, tabGrid.showScanBarcode);

            return (
                <React.Fragment>
                    <div style={{ display: 'block' }}>
                        {isVisibleTabHeader && (
                            <AntBaseForm
                                API={tabHeader.API ? tabHeader.API : props.API}
                                type={'Update'}
                                ref={header.ref}
                                formData={props.formData}
                                oldData={props.oldData}
                                table={tabHeader && tabHeader.table ? tabHeader.table : tabHeader.API ? tabHeader.API.entityName : props.table}
                                labelLocation={tabHeader.labelLocation}
                                alignItemLabelsInAllGroups={tabHeader.alignItemLabelsInAllGroups}
                                colCount={tabHeader.colCount !== undefined ? tabHeader.colCount : 1}
                                colCountDefault={tabHeader ? tabHeader.colCountDefault : undefined}
                                disabled={props.disabled ? true : tabHeader && tabHeader.disabled !== undefined ? tabHeader.disabled : undefined}
                                fields={tabHeader.fields}
                                items={tabHeader.items}
                                postFields={tabHeader.postFields}
                                currencyField={tabHeader.currencyField !== undefined ? tabHeader.currencyField : props.currencyField}
                                onFieldDataChanged={props.onFieldDataChanged}
                                onFocusItem={props.onFocusItem}
                                onError={props.onError}
                                inMultipleTabs={mainTabs && mainTabs.length > 1}
                                isSearching={tabHeader ? tabHeader.isSearching : undefined}
                            />
                        )}
                        {isVisibleTabHeader && isVisibleTabGrid && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}

                        {isVisibleTabGrid && (
                            <React.Fragment>
                                <AntGrid
                                    ref={grid.ref}
                                    API={grid.props.API ? grid.props.API : props.API}
                                    parentTable={props.formTable}
                                    parentData={props.formData}
                                    tabName={tab.name}
                                    settingsKey={tabGrid && tabGrid.settingsKey ? tabGrid.settingsKey : undefined}
                                    isTabActive={isTabActive}
                                    currencyField={tabGrid && tabGrid.currencyField !== undefined ? tabGrid.currencyField : props.currencyField}
                                    parentFK={getFKData(tabGrid)}
                                    FKField={tabGrid && tabGrid.FKField ? tabGrid.FKField : undefined}
                                    parentIDField={tabGrid && tabGrid.parentIDField ? tabGrid.parentIDField : 'code'}
                                    url={tabGrid.url}
                                    table={tabGrid && tabGrid.table ? tabGrid.table : tabGrid.API ? tabGrid.API.entityName : props.table}
                                    height={tabGrid.height !== undefined ? tabGrid.height : 'auto'}
                                    cellStyles={tabGrid ? tabGrid.cellStyles : undefined}
                                    cellRenders={tabGrid ? tabGrid.cellRenders : undefined}
                                    cellContext={tabGrid ? tabGrid.cellContext : undefined}
                                    rowStyles={tabGrid ? tabGrid.rowStyles : undefined}
                                    flashConds={tabGrid ? tabGrid.flashConds : undefined}
                                    showRowActions={tabGrid ? tabGrid.showRowActions : undefined}
                                    rowActionPack={tabGrid ? tabGrid.rowActionPack : undefined}
                                    rowActions={tabGrid ? tabGrid.rowActions : undefined}
                                    rowActionWidth={tabGrid ? tabGrid.rowActionWidth : undefined}
                                    rowActionsDisplay={tabGrid ? tabGrid.rowActionsDisplay : undefined}
                                    rowActionPrimaryActions={tabGrid ? tabGrid.rowActionPrimaryActions : undefined}
                                    batchActions={tabGrid ? tabGrid.batchActions : undefined}
                                    autoBatchActions={tabGrid ? tabGrid.autoBatchActions === true : false}
                                    onBatchExecuted={tabGrid ? tabGrid.onBatchExecuted : undefined}
                                    rowAlternationEnabled={tabGrid ? tabGrid.rowAlternationEnabled : undefined}
                                    hoverStateEnabled={tabGrid ? tabGrid.hoverStateEnabled : undefined}
                                    afterRowAction={props.onAfterAction}
                                    keyExpr={'id'}
                                    allowNew={tabGrid.allowNew}
                                    remotePaging={tabGrid.remotePaging}
                                    allowedPageSizes={
                                        tabGrid.allowedPageSizes !== undefined ? tabGrid.allowedPageSizes : DEFAULT_VOUCHER_GRID_PAGE_SIZES
                                    }
                                    defaultPageSize={
                                        tabGrid.defaultPageSize !== undefined ? tabGrid.defaultPageSize : DEFAULT_VOUCHER_GRID_PAGE_SIZE
                                    }
                                    showPageSizeSelector={false}
                                    pagerHideable={true}
                                    // Mặc định cho phép bấm header để sắp xếp (BE lấy cột bấm làm tiêu chí chính,
                                    // `ordering` của tab thành tiêu chí phụ). Lưới nhập dòng mới (allowNew) giữ tắt:
                                    // dữ liệu tại chỗ + dòng trống cuối bảng, sắp xếp sẽ xáo trộn.
                                    sortable={tabGrid.sortable !== undefined ? tabGrid.sortable : !tabGrid.allowNew}
                                    selectable={props.disabled ? false : isCanSelect ? true : undefined}
                                    crudSelectable={isCanSelect}
                                    allowEdit={props.disabled ? false : isCanEdit && (tabGrid.allowEdit !== undefined ? tabGrid.allowEdit : true)}
                                    // Nhập trực tiếp trên lưới (sửa ô + dòng mới cuối bảng) — chỉ tab khai báo inlineEdit: true.
                                    inlineEdit={!props.disabled && tabGrid.inlineEdit === true}
                                    inlineAddable={!props.disabled && Boolean(isCanAdd)}
                                    allowAutoUpdate={
                                        props.disabled ? false : isCanEdit && (tabGrid.allowAutoUpdate !== undefined ? tabGrid.allowAutoUpdate : true)
                                    }
                                    focusStateEnabled={false}
                                    fields={tabGrid.fields}
                                    columns={tabGridCols}
                                    rawColumns={tabGridRawCols}
                                    columnAutoWidth={tabGrid.columnAutoWidth}
                                    columnHidingEnabled={tabGrid.columnHidingEnabled}
                                    keyboardNavigation={tabGrid.keyboardNavigation}
                                    actionItems={tabGridActions}
                                    onAction={tabGrid.onAction}
                                    onRowAction={tabGrid.onRowAction}
                                    ordering={tabGrid.ordering}
                                    searchConditions={tabGrid.searchConditions}
                                    searchFields={searchFields.length > 0 ? searchFields : undefined}
                                    searchConditionsRequired={true}
                                    isAllowQuery={() => isGridAllowQuery(tabGrid)}
                                    onDataSourceEdited={props.onDataSourceEdited}
                                    showRefresh={tabGrid.showRefresh !== undefined ? tabGrid.showRefresh : true}
                                    showActionBar={tabGrid.showActionBar}
                                    edit={tabGrid.edit}
                                    add={tabGrid.add}
                                    delete={tabGrid.delete}
                                    import={tabGrid.import}
                                    showEdit={props.disabled ? false : showEditOnActionBar}
                                    showDelete={props.disabled ? false : showDeleteOnActionBar}
                                    canEditAction={props.disabled ? false : Boolean(showEdit)}
                                    canDeleteAction={props.disabled ? false : Boolean(showDelete)}
                                    showAdd={props.disabled ? false : Boolean(showAdd)}
                                    showImport={props.disabled ? false : Boolean(showImport)}
                                    showDownload={tabGrid.showDownload}
                                    showDetail={tabGrid.showDetail}
                                    showIndex={tabGrid.showIndex}
                                    showSelectAll={tabGrid.showSelectAll !== undefined ? tabGrid.showSelectAll : true}
                                    showFilterBySelected={tabGrid.showFilterBySelected !== undefined ? tabGrid.showFilterBySelected : false}
                                    showFieldChooser={tabGrid.showFieldChooser !== undefined ? tabGrid.showFieldChooser : true}
                                    showSearchBar={tabGrid.showSearchBar !== undefined ? tabGrid.showSearchBar : false}
                                    autoFocusSearchBar={tabGrid.autoFocusSearchBar}
                                    showScanBarcode={!props.disabled && showScanBarcode}
                                    searchBarWidth={tabGrid.searchBarWidth}
                                    onRowDblClick={(e) => {
                                        if (props.onRowDblClick) props.onRowDblClick(e, tabName, tabGrid);
                                    }}
                                    onBarcodeScanned={props.onBarcodeScanned}
                                    onDeleted={props.onAfterAction}
                                    onAdded={props.onAfterAction}
                                    onEdited={props.onAfterAction}
                                    onImported={props.onAfterAction}
                                    canImportCreate={isCanImportCreate}
                                    canImportUpdate={isCanImportUpdate}
                                    showDetailPosition={'right'}
                                    searchCategories1={tabGrid.searchCategories1}
                                    searchCategories2={tabGrid.searchCategories2}
                                    searchCategories3={tabGrid.searchCategories3}
                                    searchCategories4={tabGrid.searchCategories4}
                                    searchCategories5={tabGrid.searchCategories5}
                                    searchCategories6={tabGrid.searchCategories6}
                                    searchCategories7={tabGrid.searchCategories7}
                                    searchCategories8={tabGrid.searchCategories8}
                                    searchCategories9={tabGrid.searchCategories9}
                                    searchCategories10={tabGrid.searchCategories10}
                                    byPassCachingStates={tabGrid.byPassCachingStates !== undefined ? tabGrid.byPassCachingStates : true}
                                />
                            </React.Fragment>
                        )}
                        {isVisibleTabFooter && isVisibleTabGrid && <p style={{ marginBlockStart: '0px', marginBlockEnd: '50px' }} />}
                        {isVisibleTabFooter && (
                            <AntBaseForm
                                API={tabFooter.API ? tabFooter.API : props.API}
                                type={'Update'}
                                ref={footer.ref}
                                table={tabFooter && tabFooter.table ? tabFooter.table : tabFooter.API ? tabFooter.API.entityName : props.table}
                                formData={props.formData}
                                oldData={props.oldData}
                                alignItemLabelsInAllGroups={tabFooter.alignItemLabelsInAllGroups}
                                labelLocation={tabFooter.labelLocation}
                                colCount={tabFooter.colCount !== undefined ? tabFooter.colCount : 1}
                                colCountDefault={tabFooter ? tabFooter.colCountDefault : undefined}
                                disabled={props.disabled ? true : tabFooter && tabFooter.disabled !== undefined ? tabFooter.disabled : undefined}
                                fields={tabFooter.fields}
                                items={tabFooter.items}
                                postFields={tabFooter.postFields}
                                currencyField={tabFooter.currencyField !== undefined ? tabFooter.currencyField : props.currencyField}
                                onFieldDataChanged={props.onFieldDataChanged}
                                onFocusItem={props.onFocusItem}
                                onError={props.setError}
                                inMultipleTabs={mainTabs && mainTabs.length > 1}
                                isSearching={tabFooter ? tabFooter.isSearching : undefined}
                            />
                        )}
                    </div>
                </React.Fragment>
            );
        },
        [props.formData, props.tabs, mainTabs, props.shouldRender, props.onRowDblClick, getFKData, shouldRender, isRender, isTabActive]
    );

    const validate = useCallback(async () => {
        var isOK = true;

        for (let i = 0; i < tabHeaders.current.length; i++) {
            const header = tabHeaders.current[i];
            if (header && header.ref && header.ref.current) {
                var isValid = await header.ref.current.validate();
                if (!isValid) isOK = false;
            }
        }

        for (let i = 0; i < tabFooters.current.length; i++) {
            const footer = tabFooters.current[i];
            if (footer && footer.ref && footer.ref.current) {
                var isValid = await footer.ref.current.validate();
                if (!isValid) isOK = false;
            }
        }

        return isOK;
    }, []);

    const reload = useCallback((opts) => {
        tabGrids.current.forEach((grid) => grid?.ref?.current?.refresh?.(opts));
    }, []);

    // Socket có thể đổi dòng con mà snapshot đầu phiếu vẫn y hệt. Làm mới các
    // lưới đang hiển thị; lưới thực sự đóng sẽ nạp khi người dùng mở lại.
    const invalidateGrids = useCallback(() => {
        tabGrids.current.forEach((grid) => {
            const instance = grid?.ref?.current;
            if (!instance) return;
            if (isTabActive(grid.tabName)) instance.refresh?.(true);
            else instance.markPendingReload?.();
        });
    }, [isTabActive]);

    useImperativeHandle(ref, () => ({
        validate: validate,
        forceRender: forceRender,
        reload: reload,
        invalidateGrids: invalidateGrids
    }));

    // useEffect(() => {
    //     forceRender();
    // }, [props.formData]);

    useEffect(() => {
        return () => {
            for (let i = 0; i < tabHeaders.current.length; i++) {
                delete tabHeaders[i];
            }
            for (let i = 0; i < tabFooters.current.length; i++) {
                delete tabFooters[i];
            }
            for (let i = 0; i < tabGrids.current.length; i++) {
                delete tabGrids[i];
            }

            delete tabHeaders.current;
            delete tabFooters.current;
            delete tabGrids.current;
            tabHeaders.current = null;
            tabFooters.current = null;
            tabGrids.current = null;
        };
    }, []);

    return (
        <React.Fragment>
            {/* {isRender && ( */}
            <AntTabs
                tabs={mainTabs}
                shouldRender={isRender}
                rawTabs={props.tabs}
                formData={props.formData}
                tabStyle={props.tabStyle}
                height={props.tabHeight ? props.tabHeight : 520}
                onRenderTabContent={onRenderTab}
                stableItems={true}
                onTabChanged={onTabChanged}
                autoHideTitle={props.autoHideTitle !== undefined ? props.autoHideTitle : true}
                // animationDuration={330}
                autoExpandAll={props.autoExpandAll !== undefined ? props.autoExpandAll : true}
                groupCollapsible={props.groupCollapsible}
                groupExpandMultiple={props.groupExpandMultiple !== undefined ? props.groupExpandMultiple : false}
                visible={props.visible}
                lazyLoading={false}
            />
            {/* )} */}
        </React.Fragment>
    );
});
